import jsPDF from 'jspdf'

/**
 * Export session results to CSV format
 */
export const exportToCSV = (rounds, sessionCode, currentTopic, currentIssue, players, revealed) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  const filename = `planning-poker-${sessionCode}-${timestamp}.csv`

  let csvContent = 'Planning Poker Export\n'
  csvContent += `Session Code,${sessionCode}\n`
  csvContent += `Export Date,${new Date().toLocaleString()}\n\n`

  // Current session data
  if (currentTopic) {
    csvContent += 'CURRENT SESSION\n'
    csvContent += `Topic,${escapeCSV(currentTopic)}\n`
    if (currentIssue) {
      csvContent += `Issue Platform,${currentIssue.platform}\n`
      csvContent += `Issue Number,${currentIssue.number || ''}\n`
      csvContent += `Issue Title,${escapeCSV(currentIssue.title || '')}\n`
      csvContent += `Issue URL,${currentIssue.url}\n`
    }
    csvContent += `Total Players,${players.length}\n`

    if (revealed && players.length > 0) {
      csvContent += '\nPlayer,Vote\n'
      players.forEach(player => {
        csvContent += `${escapeCSV(player.name)},${player.vote !== null ? player.vote : 'No vote'}\n`
      })

      // Calculate stats
      const numericVotes = players
        .filter(p => p.vote !== null && !isNaN(parseFloat(p.vote)))
        .map(p => parseFloat(p.vote))

      if (numericVotes.length > 0) {
        const avg = numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length
        const sorted = [...numericVotes].sort((a, b) => a - b)
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)]

        csvContent += '\nSTATISTICS\n'
        csvContent += `Average,${avg.toFixed(2)}\n`
        csvContent += `Median,${median}\n`
        csvContent += `Min,${Math.min(...numericVotes)}\n`
        csvContent += `Max,${Math.max(...numericVotes)}\n`
      }
    }
    csvContent += '\n'
  }

  // Round history
  if (rounds.length > 0) {
    csvContent += 'ROUND HISTORY\n\n'

    rounds.forEach((round, idx) => {
      csvContent += `Round ${rounds.length - idx}\n`
      csvContent += `Topic,${escapeCSV(round.topic)}\n`

      if (round.issue_url) {
        csvContent += `Issue Platform,${round.issue_platform}\n`
        csvContent += `Issue Number,${round.issue_number || ''}\n`
        csvContent += `Issue Title,${escapeCSV(round.issue_title || '')}\n`
        csvContent += `Issue URL,${round.issue_url}\n`
      }

      csvContent += `Completed,${new Date(round.completed_at).toLocaleString()}\n`
      csvContent += `Participants,${round.votes.filter(v => v.vote !== null).length}\n`

      if (round.average !== null) {
        csvContent += `Average,${Number(round.average).toFixed(2)}\n`
        csvContent += `Median,${round.median}\n`
      }

      csvContent += '\nPlayer,Vote\n'
      round.votes
        .filter(v => v.vote !== null)
        .forEach(vote => {
          csvContent += `${escapeCSV(vote.name)},${vote.vote}\n`
        })

      csvContent += '\n'
    })
  }

  downloadFile(csvContent, filename, 'text/csv;charset=utf-8;')
}

/**
 * Export session results to JSON format
 */
export const exportToJSON = (rounds, sessionCode, currentTopic, currentIssue, players, revealed) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  const filename = `planning-poker-${sessionCode}-${timestamp}.json`

  const data = {
    sessionCode,
    exportDate: new Date().toISOString(),
    currentSession: null,
    roundHistory: []
  }

  // Current session data
  if (currentTopic) {
    const currentSession = {
      topic: currentTopic,
      issue: currentIssue,
      totalPlayers: players.length,
      players: revealed ? players.map(p => ({
        name: p.name,
        vote: p.vote
      })) : null
    }

    if (revealed && players.length > 0) {
      const numericVotes = players
        .filter(p => p.vote !== null && !isNaN(parseFloat(p.vote)))
        .map(p => parseFloat(p.vote))

      if (numericVotes.length > 0) {
        const avg = numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length
        const sorted = [...numericVotes].sort((a, b) => a - b)
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)]

        currentSession.statistics = {
          average: Number(avg.toFixed(2)),
          median: median,
          min: Math.min(...numericVotes),
          max: Math.max(...numericVotes)
        }
      }
    }

    data.currentSession = currentSession
  }

  // Round history
  data.roundHistory = rounds.map((round, idx) => ({
    roundNumber: rounds.length - idx,
    topic: round.topic,
    issue: round.issue_url ? {
      platform: round.issue_platform,
      number: round.issue_number,
      title: round.issue_title,
      url: round.issue_url
    } : null,
    completedAt: round.completed_at,
    participants: round.votes.filter(v => v.vote !== null).length,
    statistics: round.average !== null ? {
      average: Number(round.average),
      median: round.median
    } : null,
    votes: round.votes
      .filter(v => v.vote !== null)
      .map(v => ({
        name: v.name,
        vote: v.vote
      }))
  }))

  const jsonString = JSON.stringify(data, null, 2)
  downloadFile(jsonString, filename, 'application/json')
}

/**
 * Export session results to PDF format
 */
export const exportToPDF = (rounds, sessionCode, currentTopic, currentIssue, players, revealed) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
  const filename = `planning-poker-${sessionCode}-${timestamp}.pdf`

  const doc = new jsPDF()
  let yPos = 20

  // Header
  doc.setFontSize(20)
  doc.text('Planning Poker Export', 20, yPos)
  yPos += 10

  doc.setFontSize(10)
  doc.text(`Session Code: ${sessionCode}`, 20, yPos)
  yPos += 6
  doc.text(`Export Date: ${new Date().toLocaleString()}`, 20, yPos)
  yPos += 15

  // Current Session
  if (currentTopic) {
    doc.setFontSize(16)
    doc.text('Current Session', 20, yPos)
    yPos += 8

    doc.setFontSize(10)
    doc.text(`Topic: ${currentTopic}`, 20, yPos)
    yPos += 6

    if (currentIssue) {
      doc.text(`Issue: ${currentIssue.platform} ${currentIssue.number || ''} - ${currentIssue.title || ''}`, 20, yPos)
      yPos += 6
      doc.text(`URL: ${currentIssue.url}`, 20, yPos)
      yPos += 6
    }

    doc.text(`Total Players: ${players.length}`, 20, yPos)
    yPos += 10

    if (revealed && players.length > 0) {
      doc.setFontSize(12)
      doc.text('Votes:', 20, yPos)
      yPos += 6

      doc.setFontSize(10)
      players.forEach(player => {
        if (yPos > 270) {
          doc.addPage()
          yPos = 20
        }
        doc.text(`${player.name}: ${player.vote !== null ? player.vote : 'No vote'}`, 25, yPos)
        yPos += 6
      })

      // Statistics
      const numericVotes = players
        .filter(p => p.vote !== null && !isNaN(parseFloat(p.vote)))
        .map(p => parseFloat(p.vote))

      if (numericVotes.length > 0) {
        const avg = numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length
        const sorted = [...numericVotes].sort((a, b) => a - b)
        const median = sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)]

        yPos += 5
        doc.setFontSize(12)
        doc.text('Statistics:', 20, yPos)
        yPos += 6

        doc.setFontSize(10)
        doc.text(`Average: ${avg.toFixed(2)}`, 25, yPos)
        yPos += 6
        doc.text(`Median: ${median}`, 25, yPos)
        yPos += 6
        doc.text(`Min: ${Math.min(...numericVotes)}`, 25, yPos)
        yPos += 6
        doc.text(`Max: ${Math.max(...numericVotes)}`, 25, yPos)
        yPos += 10
      }
    }
  }

  // Round History
  if (rounds.length > 0) {
    if (yPos > 250) {
      doc.addPage()
      yPos = 20
    }

    doc.setFontSize(16)
    doc.text('Round History', 20, yPos)
    yPos += 10

    rounds.forEach((round, idx) => {
      if (yPos > 250) {
        doc.addPage()
        yPos = 20
      }

      doc.setFontSize(12)
      doc.text(`Round ${rounds.length - idx}`, 20, yPos)
      yPos += 6

      doc.setFontSize(10)
      doc.text(`Topic: ${round.topic}`, 25, yPos)
      yPos += 6

      if (round.issue_url) {
        doc.text(`Issue: ${round.issue_platform} ${round.issue_number || ''} - ${round.issue_title || ''}`, 25, yPos)
        yPos += 6
      }

      doc.text(`Completed: ${new Date(round.completed_at).toLocaleString()}`, 25, yPos)
      yPos += 6

      doc.text(`Participants: ${round.votes.filter(v => v.vote !== null).length}`, 25, yPos)
      yPos += 6

      if (round.average !== null) {
        doc.text(`Average: ${Number(round.average).toFixed(2)} | Median: ${round.median}`, 25, yPos)
        yPos += 6
      }

      doc.text('Votes:', 25, yPos)
      yPos += 6

      const votesText = round.votes
        .filter(v => v.vote !== null)
        .map(v => `${v.name}: ${v.vote}`)
        .join(', ')

      // Handle long text wrapping
      const lines = doc.splitTextToSize(votesText, 160)
      lines.forEach(line => {
        if (yPos > 270) {
          doc.addPage()
          yPos = 20
        }
        doc.text(line, 30, yPos)
        yPos += 6
      })

      yPos += 5
    })
  }

  doc.save(filename)
}

/**
 * Helper function to escape CSV values
 */
const escapeCSV = (str) => {
  if (str === null || str === undefined) return ''
  const stringValue = String(str)
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return stringValue
}

/**
 * Helper function to trigger file download
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}
